import type { UpdateStorefrontReviewVariables } from '@api/graphql/reviews/updateReview';
import type {
	ReviewFormSubmission,
	ReviewPhoto,
} from '@components/reviews/components/reviews_modal_ver_two/types';
import { Logger } from '@utils/logger/logger';
import Joi from 'joi';

interface Params {
	data: ReviewFormSubmission;
	reviewId: string;
}

const querySchema = Joi.object<Required<Params>>({
	data: Joi.object({
		content: Joi.string().min(30).required(),
		firstName: Joi.string().optional(),
		lastName: Joi.string().optional(),
		gallery: Joi.array()
			.items(
				Joi.object({
					source: Joi.object(),
					sourceId: Joi.string(),
					typeCode: Joi.string(),
				}),
			)
			.optional(),
		rating: Joi.number().integer().min(1).max(5).required(),
		budget: Joi.object({
			cost: Joi.number().optional(),
			guestCount: Joi.number().optional(),
		}).optional(),
	}),
	reviewId: Joi.string().required(),
}).options({ allowUnknown: true });

const ratingsTypes = {
	responseTime: '0495589c-f9e4-4d59-8266-e7e20e454851',
	quality: '176e4e0a-73c9-49a3-ad20-8a77fa004413',
	value: '5089222e-b87d-4ee2-841e-071440b37e86',
	flexibility: '51b279f9-559e-4149-a01c-a4a22d03f179',
	professionalism: 'f24e2d90-3aec-48a2-97ef-eb27264edebe',
};

const mapRating = (rating: number) => {
	return Object.values(ratingsTypes).map((value) => ({
		ratingId: value,
		value: rating,
	}));
};

const mapAttributes = (data: ReviewFormSubmission) => {
	return {
		budgetCost: data.budget?.cost,
		budgetGuestCount: data.budget?.guestCount,
	};
};

const mapPhotos = (gallery: ReviewPhoto[]) => {
	return gallery.map((photo, index) => ({
		id: photo.id,
		sourceId: photo.sourceId,
		sortOrder: gallery.length - index,
		active: photo.active,
		height: photo.height || null,
		width: photo.width || null,
	}));
};

export const buildVariablesForUpdateReview = async (
	params: Params,
): Promise<UpdateStorefrontReviewVariables> => {
	try {
		const { reviewId, data } = await querySchema.validateAsync(params);

		return {
			reviewId,
			content: data.content,
			ratings: mapRating(data.rating),
			attributes: mapAttributes(data),
			photos: mapPhotos(data.gallery),
			firstName: data.firstName,
			lastName: data.lastName,
		};
	} catch (e) {
		Logger.error({
			errorToLog: e,
			newRelic: true,
			log_message: 'Error in buildVariables function for updateReview',
		});
		throw e;
	}
};
